import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Stethoscope, Loader2, Save, Trash2 } from 'lucide-react';
import { BACKEND_URL } from 'apiConfig';

const API = BACKEND_URL;
const getToken = () => localStorage.getItem('token');

const STAGES = [
  { value: 'medical_student', label: 'Medical student' },
  { value: 'resident', label: 'Resident' },
  { value: 'fellow', label: 'Fellow' },
  { value: 'attending', label: 'Attending / Consultant' },
  { value: 'app_pa_np', label: 'APP / PA / NP' },
  { value: 'researcher', label: 'Researcher' },
  { value: 'other', label: 'Other' },
];
const YEARS = [
  { value: 'in_training', label: 'In training' },
  { value: '0_2', label: '0\u20132 years' },
  { value: '3_5', label: '3\u20135 years' },
  { value: '6_10', label: '6\u201310 years' },
  { value: '11_20', label: '11\u201320 years' },
  { value: '21_plus', label: '21+ years' },
];
const SETTINGS = [
  { value: 'academic', label: 'Academic medical center' },
  { value: 'community_hospital', label: 'Community hospital' },
  { value: 'private_practice', label: 'Private practice' },
  { value: 'group_practice', label: 'Group practice' },
  { value: 'government_va', label: 'Government / VA / public health' },
  { value: 'research', label: 'Research-focused' },
  { value: 'other', label: 'Other' },
];
const ENVIRONMENTS = [
  { value: 'inpatient', label: 'Inpatient' },
  { value: 'outpatient', label: 'Outpatient' },
  { value: 'mixed', label: 'Mixed' },
  { value: 'not_clinical', label: 'Not primarily clinical' },
];
const COUNTRIES = [
  'United States','United Kingdom','Canada','Australia','India','Germany','France','Brazil',
  'Japan','South Korea','China','Italy','Spain','Netherlands','Sweden','Switzerland','Singapore',
  'Israel','South Africa','Mexico','Saudi Arabia','UAE','Nigeria','Egypt','Turkey','Pakistan',
  'New Zealand','Ireland','Belgium','Austria','Denmark','Norway','Finland','Poland','Portugal',
  'Czech Republic','Argentina','Colombia','Chile','Other',
];
const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware',
  'Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky',
  'Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri',
  'Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York',
  'North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island',
  'South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington',
  'West Virginia','Wisconsin','Wyoming','District of Columbia',
];

export default function PracticeProfileEditor() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [form, setForm] = useState({
    current_stage: '', years_in_practice: '',
    country: '', state_province: '', city: '',
    practice_setting: '', clinical_environment: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/practice-profile`, { headers: { Authorization: `Bearer ${getToken()}` } });
        const data = await res.json();
        if (data.practice_profile) {
          const p = data.practice_profile;
          setForm(prev => ({ 
            ...prev, 
            current_stage: p.current_stage || '',
            years_in_practice: p.years_in_practice || '',
            country: p.country || '',
            state_province: p.state_province || '',
            city: p.city || '',
            practice_setting: p.practice_setting || '',
            clinical_environment: p.clinical_environment || '',
          }));
          // Auto-expand if user has filled in fields
          const hasData = p.current_stage || p.years_in_practice || p.country || p.practice_setting;
          if (hasData) setIsExpanded(true);
        }
      } catch {}
      setLoading(false);
    })();
  }, []);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const showStateDropdown = form.country === 'United States';
  const fieldCount = Object.entries(form).filter(([k, v]) => v && v.trim()).length;

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleaned = {};
      for (const [k, v] of Object.entries(form)) {
        if (v && v.trim()) {
          cleaned[k] = v;
        }
      }
      await fetch(`${API}/api/practice-profile`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ practice_profile: Object.keys(cleaned).length > 0 ? cleaned : null }),
      });
      toast.success('Practice profile updated');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/practice-profile`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ practice_profile: null }),
      });
      setForm({
        current_stage: '', years_in_practice: '', country: '', state_province: '',
        city: '', practice_setting: '', clinical_environment: '',
      });
      toast.success('Practice profile cleared');
    } catch { toast.error('Failed to clear'); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>;

  return (
    <Card className="border-slate-200 bg-slate-50/50" data-testid="practice-profile-editor">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-100/50 transition-colors rounded-t-lg"
      >
        <div className="flex items-center gap-2">
          <Stethoscope className="h-4 w-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-600">Practice Profile</span>
          {fieldCount > 0 && (
            <Badge className="bg-slate-200 text-slate-600 text-[10px] px-1.5 py-0">{fieldCount} fields</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Optional</span>
          <svg
            className={`h-4 w-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Collapsible Content */}
      {isExpanded && (
        <CardContent className="pt-0 pb-4 px-4 space-y-4">
          <p className="text-xs text-slate-500">Help personalize your experience. All fields are optional.</p>
          
          {/* Stage + Years */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Current Stage</Label>
              <Select value={form.current_stage} onValueChange={v => update('current_stage', v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="edit-stage"><SelectValue placeholder="Select stage" /></SelectTrigger>
                <SelectContent>{STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Years in Practice</Label>
              <Select value={form.years_in_practice} onValueChange={v => update('years_in_practice', v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="edit-years"><SelectValue placeholder="Select range" /></SelectTrigger>
                <SelectContent>{YEARS.map(y => <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Country + State + City */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Country</Label>
              <Select value={form.country} onValueChange={v => { update('country', v); update('state_province', ''); }}>
                <SelectTrigger className="h-9 text-sm" data-testid="edit-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent>{COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">State / Province</Label>
              {showStateDropdown ? (
                <Select value={form.state_province} onValueChange={v => update('state_province', v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="edit-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input className="h-9 text-sm" value={form.state_province} onChange={e => update('state_province', e.target.value)} placeholder="Province / region" data-testid="edit-state-input" />
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">City</Label>
              <Input className="h-9 text-sm" value={form.city} onChange={e => update('city', e.target.value)} placeholder="City" data-testid="edit-city" />
            </div>
          </div>

          {/* Setting + Environment */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Practice Setting</Label>
              <Select value={form.practice_setting} onValueChange={v => update('practice_setting', v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="edit-setting"><SelectValue placeholder="Select setting" /></SelectTrigger>
                <SelectContent>{SETTINGS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Clinical Environment</Label>
              <Select value={form.clinical_environment} onValueChange={v => update('clinical_environment', v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="edit-env"><SelectValue placeholder="Select environment" /></SelectTrigger>
                <SelectContent>{ENVIRONMENTS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-slate-200">
            <Button variant="ghost" size="sm" onClick={handleClear} disabled={saving} className="text-slate-400 hover:text-slate-600 h-8 text-xs" data-testid="practice-clear-btn">
              <Trash2 className="h-3 w-3 mr-1" /> Clear
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="h-8 text-xs" data-testid="practice-save-btn">
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
              Save
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
